import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoInternalProduct } from "@redotech/redo-billing-common/redo-internal-products";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";

import { createContext, memo } from "react";

export interface OrderManagementSuiteStatuses {
  returnsEnabled: boolean;
  warrantiesEnabled: boolean;
  warrantyRegistrationEnabled: boolean;
  recycleEnabled: boolean;
  withdrawalEnabled: boolean;
  omsEnabled: boolean;
  omsActive: boolean;
  packageProtectionEnabled: boolean;
  wmsEnabled: boolean;
  wmsSourceOfTruthEnabled: boolean;
}

export const OrderManagementSuiteStatusesContext =
  createContext<OrderManagementSuiteStatuses>({
    returnsEnabled: false,
    warrantiesEnabled: false,
    warrantyRegistrationEnabled: false,
    recycleEnabled: false,
    withdrawalEnabled: false,
    omsEnabled: false,
    omsActive: false,
    packageProtectionEnabled: false,
    wmsEnabled: false,
    wmsSourceOfTruthEnabled: false,
  });

export const OrderManagementSuiteStatusesProvider = memo(
  function OrderManagementSuiteStatusesProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const team = useRequiredContext(TeamContext);
    const settings = team.settings;

    const returnsEnabled = !!settings.returns?.enabled;
    const warrantiesEnabled = team?.settings.warranties?.enabled;
    const omsEnabled = !!settings.outboundLabels?.newOmsEnabled;
    const omsActive =
      !!team?.redoInternalProducts?.status?.[RedoInternalProduct.OMS]?.isActive;
    const packageProtectionEnabled = !!settings.packageProtection?.enabled;
    const warrantyRegistrationEnabled =
      !!team?.settings.warranties?.registrationEnabled;
    const recycleEnabled =
      (settings.recycling?.enabled ?? !!settings.convertRecyclingProducts) &&
      !!settings.enableNonShopifyClaims;
    const withdrawalEnabled =
      !!team?.settings.withdrawals?.enabled &&
      team?.featureFlags?.[FeatureFlagKey.WITHDRAWALS_FLOW] === true;

    const wmsEnabled = !!settings?.outboundLabels?.wms?.enabled;
    const wmsSourceOfTruthEnabled =
      !!settings?.outboundLabels?.wms?.sourceOfTruthEnabled;

    const orderManagementSuiteStatuses = {
      returnsEnabled,
      warrantiesEnabled,
      omsEnabled,
      omsActive,
      packageProtectionEnabled,
      warrantyRegistrationEnabled,
      recycleEnabled,
      withdrawalEnabled,
      wmsEnabled,
      wmsSourceOfTruthEnabled,
    };

    return (
      <OrderManagementSuiteStatusesContext.Provider
        value={orderManagementSuiteStatuses}
      >
        {children}
      </OrderManagementSuiteStatusesContext.Provider>
    );
  },
);

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RETURNS_AGENT_ICON } from "@redotech/redo-icons/ai-star.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as CLAIMS_ICON } from "@redotech/redo-icons/alert-hexagon.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as CLAIMS_ICON_FILLED } from "@redotech/redo-icons/alert-hexagon_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RATE_CALCULATOR_ICON } from "@redotech/redo-icons/calculator.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RATE_CALCULATOR_ICON_FILLED } from "@redotech/redo-icons/calculator_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as EOD_MANIFEST_ICON } from "@redotech/redo-icons/file-06.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as EOD_MANIFEST_ICON_FILLED } from "@redotech/redo-icons/file-06_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  default as FULFILLMENT_ICON,
  default as ORDERS_ICON,
} from "@redotech/redo-icons/package.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  default as FULFILLMENT_ICON_FILLED,
  default as ORDERS_ICON_FILLED,
} from "@redotech/redo-icons/package_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as BATCH_HISTORY_ICON } from "@redotech/redo-icons/packages.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as BATCH_HISTORY_ICON_FILLED } from "@redotech/redo-icons/packages_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as ORDER_EDITING_ICON } from "@redotech/redo-icons/pencil-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as ORDER_EDITING_ICON_FILLED } from "@redotech/redo-icons/pencil-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as PRINT_JOBS_ICON } from "@redotech/redo-icons/printer.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as PRINT_JOBS_ICON_FILLED } from "@redotech/redo-icons/printer_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as SCAN_ICON } from "@redotech/redo-icons/qr-code-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as SCAN_ICON_FILLED } from "@redotech/redo-icons/qr-code-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RECYCLE_ICON_FILLED } from "@redotech/redo-icons/recycle-filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RECYCLE_ICON } from "@redotech/redo-icons/recycle.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as WARRANTIES_ICON } from "@redotech/redo-icons/shield-tick.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as WARRANTIES_ICON_FILLED } from "@redotech/redo-icons/shield-tick_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as PICK_SESSIONS_ICON } from "@redotech/redo-icons/shopping-bag-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as PICK_SESSIONS_ICON_FILLED } from "@redotech/redo-icons/shopping-bag-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as PERSONALIZED_INSERTS_ICON } from "@redotech/redo-icons/sticker-square.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as PERSONALIZED_INSERTS_ICON_FILLED } from "@redotech/redo-icons/sticker-square_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RECOGNIZE_ICON } from "@redotech/redo-icons/trophy-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as RECOGNIZE_ICON_FILLED } from "@redotech/redo-icons/trophy-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as IMS_ICON } from "@redotech/redo-icons/tag-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as IMS_ICON_FILLED } from "@redotech/redo-icons/tag-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as AEO_ICON } from "@redotech/redo-icons/target-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as AEO_ICON_FILLED } from "@redotech/redo-icons/target-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as OUTBOUND_ICON } from "@redotech/redo-icons/truck-01.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as OUTBOUND_ICON_FILLED } from "@redotech/redo-icons/truck-01_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as WAREHOUSE_ICON } from "@redotech/redo-icons/warehouse.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as WAREHOUSE_ICON_FILLED } from "@redotech/redo-icons/warehouse_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as WITHDRAWAL_ICON } from "@redotech/redo-icons/x-circle.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { default as WITHDRAWAL_ICON_FILLED } from "@redotech/redo-icons/x-circle_filled.svg";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  default as RETURNS_ICON,
  default as RETURNS_ICON_FILLED,
} from "@redotech/redo-web/icon-old/flip-backward.svg";
